import { DEFAULT_TEXT_TRANSCRIPT_CONFIG } from '@src/services/karaoke/karaokeConstants';
import { isNoneColor } from '@src/utility/color';

import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';

/** A caption colour the bar offers its own button for. */
export interface CaptionColorSlot {
  property:
    | 'fill_color'
    | 'transcript_color'
    | 'background_color'
    | 'stroke_color';
  /** The button's label and tooltip (Title Case, as UI labels are). */
  label: string;
  /** The colour as stored. */
  value: string;
}

/** Animation styles that paint the spoken words in `transcript_color`. */
const COLOURING_EFFECTS = new Set(['color', 'karaoke', 'highlight']);

const hasStroke = (config: Partial<KaraokeConfig>): boolean => {
  const width = parseFloat(String(config.stroke_width ?? ''));
  return (
    Number.isFinite(width) && width > 0 && !isNoneColor(config.stroke_color)
  );
};

/**
 * One colour button per colour the caption style actually uses — the text,
 * the animated words' colour when the animation style paints them, the
 * background when there is one, the outline when it is drawn — in that order
 * (the order the Captions panel lists them). A style with no background gets
 * no Background button: the bar shows the colours you can see. Shadow stays
 * panel-only (multi-field).
 *
 * `keep` holds buttons the bar already showed for this caption: they stay
 * even once their colour is erased, so it can be picked again.
 */
export function captionColorSlots(
  config: Partial<KaraokeConfig>,
  keep: ReadonlySet<CaptionColorSlot['property']> = new Set(),
): CaptionColorSlot[] {
  const slots: CaptionColorSlot[] = [
    {
      property: 'fill_color',
      label: 'Text Color',
      value: config.fill_color || '#ffffff',
    },
  ];
  const add = (
    property: Exclude<CaptionColorSlot['property'], 'fill_color'>,
    label: string,
    visible: boolean,
    value = config[property] || '',
  ) => {
    if (visible || keep.has(property)) {
      slots.push({ property, label, value });
    }
  };
  // Unset, the words still paint in the renderer's default colour.
  const highlight =
    config.transcript_color || DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_color;
  add(
    'transcript_color',
    'Highlight Color',
    !!config.transcript_effect &&
      COLOURING_EFFECTS.has(config.transcript_effect) &&
      !isNoneColor(highlight),
    highlight,
  );
  add(
    'background_color',
    'Background Color',
    !isNoneColor(config.background_color),
  );
  add('stroke_color', 'Outline Color', hasStroke(config));
  return slots;
}
